import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonComponent from "./ButtonMC";
import LoadingMC from "./LoadingMC";
import ConnectWithWsModal from "../modules/Dashboard/ConnectWithWsModal";
import usePlatformIntegrationStatus from "../modules/Integrations/usePlatformIntegrationStatus";
import PLATFORM_CONFIG from "../utils/platformConfig";
// eslint-disable-next-line import/order, import/no-unresolved
import emptyInboxImg from "images/emptyScreenInbox.png";

const ICON_SIZE_CLASS = "!tw-h-4 !tw-w-4";

/** UI-only: CTA copy, icons and navigation. Connection status uses usePlatformIntegrationStatus. */
const PLATFORM_UI_CONFIG = {
  whatsapp: {
    statusPlatform: "whatsapp",
    connectTextKey: "inbox.connect_whatsapp",
    icon: "whatsapp-fill-outline",
    iconClass: `tw-text-green-400 ${ICON_SIZE_CLASS}`,
    isModal: true,
    path: "whatsapp_business",
    class_name: "tw-h-full",
    is_inbox: true
  },
  messenger: {
    statusPlatform: "messenger",
    connectTextKey: "inbox.connect_messenger",
    icon: PLATFORM_CONFIG.messenger.icon,
    iconClass: `${PLATFORM_CONFIG.messenger.color} ${ICON_SIZE_CLASS}`,
    path: "messenger",
    class_name: "tw-h-full",
    is_inbox: true
  },
  instagram: {
    statusPlatform: "instagram",
    connectTextKey: "inbox.connect_instagram",
    icon: PLATFORM_CONFIG.instagram.icon,
    iconClass: `${PLATFORM_CONFIG.instagram.color} ${ICON_SIZE_CLASS}`,
    path: "instagram",
    class_name: "tw-h-full",
    is_inbox: true
  },
  facebook_comments: {
    statusPlatform: "facebook_comments",
    connectTextKey: "inbox.connect_facebook_comments",
    icon: PLATFORM_CONFIG.facebook_comments.cssIcon,
    iconClass: `${PLATFORM_CONFIG.facebook_comments.color} ${ICON_SIZE_CLASS}`,
    path: "facebook_comments",
    class_name: "tw-h-full",
    is_inbox: true
  },
  instagram_comments: {
    statusPlatform: "instagram_comments",
    connectTextKey: "inbox.connect_instagram_comments",
    icon: PLATFORM_CONFIG.instagram_comments.cssIcon,
    iconClass: `${PLATFORM_CONFIG.instagram_comments.color} ${ICON_SIZE_CLASS}`,
    path: "instagram_comments",
    class_name: "tw-h-full",
    is_inbox: true
  },
  whatsapp_modal: {
    statusPlatform: "whatsapp",
    connectTextKey: "inbox.connect_whatsapp",
    icon: "whatsapp-fill-outline",
    iconClass: `tw-text-green-400 ${ICON_SIZE_CLASS}`,
    isModal: true,
    path: "whatsapp_business",
    class_name: "",
    is_inbox: false
  },
  tik_tok_messages: {
    statusPlatform: "tik_tok_messages",
    connectTextKey: "inbox.connect_tiktok",
    icon: PLATFORM_CONFIG.tik_tok_messages.icon,
    iconClass: `${PLATFORM_CONFIG.tik_tok_messages.color} ${ICON_SIZE_CLASS}`,
    path: "tiktok_messages",
    class_name: "tw-h-full",
    is_inbox: true
  },
  tik_tok_comments: {
    statusPlatform: "tik_tok_comments",
    connectTextKey: "inbox.connect_tiktok",
    icon: PLATFORM_CONFIG.tik_tok_comments.icon,
    iconClass: `${PLATFORM_CONFIG.tik_tok_comments.color} ${ICON_SIZE_CLASS}`,
    path: "tiktok_comments",
    class_name: "tw-h-full",
    is_inbox: true
  },
  tiktok: {
    statusPlatform: "tik_tok_messages",
    connectTextKey: "inbox.connect_tiktok",
    icon: PLATFORM_CONFIG.tik_tok_messages.icon,
    iconClass: `${PLATFORM_CONFIG.tik_tok_messages.color} ${ICON_SIZE_CLASS}`,
    path: "tiktok_messages",
    class_name: "tw-h-full",
    is_inbox: true
  }
};

const EmptyScreenInboxMC = ({ platform, customTitle = '' }) => {
  const { t } = useTranslation("EmptyScreensMC");
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const [openConnectWithWsModal, setOpenConnectWithWsModal] = useState(false);

  const config = PLATFORM_UI_CONFIG[platform];
  const {
    isPlatformConnected,
    isLoadingPlatformStatus,
    retailerInfoWithIntegrations
  } = usePlatformIntegrationStatus(config?.statusPlatform, retailer_info);

  if (!config) return null;

  if (isLoadingPlatformStatus) {
    return (
      <LoadingMC
        loading
        className="tw-h-full tw-w-full"
      />
    );
  }

  if (isPlatformConnected) return null;

  const retailerSlug = retailerInfoWithIntegrations?.slug || retailer_info?.slug;

  const handleConnect = () => {
    if (config.isModal) {
      setOpenConnectWithWsModal(true);
    } else if (retailerSlug) {
      window.location.href = `/retailers/${retailerSlug}/integrations/${config.path}`;
    }
  };

  return (
    <div className={`tw-wrapper tw-h-full ${config.is_inbox ? 'messaging-container' : ''}`}>
      <div className={config.class_name}>
        <div className="tw-flex tw-items-start tw-justify-center tw-h-full tw-bg-white tw-flex-1 tw-w-full tw-py-12">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-2/3 tw-gap-4 tw-my-12 tw-pt-8">
            <p className="tw-text-gray-950 tw-text-sm tw-font-medium tw-mb-2 tw-text-center">
              {customTitle ? t(customTitle) : t("inbox.title")}
            </p>
            <img
              src={emptyInboxImg}
              alt="Empty Inbox"
              className="tw-w-64 tw-h-auto tw-object-contain"
            />
            <ButtonComponent
              icon={config.icon}
              text={t(config.connectTextKey)}
              onClick={handleConnect}
              className="tw-font-medium tw-px-6 !tw-py-1 !tw-text-default !tw-gap-2"
              iconClassName={config.iconClass}
              size="large"
            />
          </div>
        </div>

        {config.isModal && openConnectWithWsModal && (
          <div className="tw-fixed tw-inset-0 tw-z-[9999]">
            <ConnectWithWsModal onClose={() => setOpenConnectWithWsModal(false)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmptyScreenInboxMC;
